import { collection, getDocs, getDoc, doc, collectionGroup, query, limit } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { logError, logInfo } from '../logger';
import { auth, app } from '../../config/firebase';

/**
 * Generates a complete schema of the Firestore database
 * @returns {Promise<Object>} The database schema
 */
export const generateDatabaseSchema = async () => {
  const schema = {
    metadata: {
      generatedAt: Date.now(),
      totalCollections: 0,
      totalDocuments: 0,
      scanDuration: 0,
      progress: {
        scannedCollections: 0,
        totalCollections: 0,
        currentCollection: '',
        status: 'initializing'
      }
    },
    collections: {}
  };

  const startTime = Date.now();
  const processedCollections = new Set();

  try {
    // Log start of schema generation
    logInfo('Starting database schema generation');
    
    // Check if user is admin
    const isAdmin = auth.currentUser?.email === 'jfprince@soltec.ca' || 
                   auth.currentUser?.email === 'jfprince29@gmail.com' ||
                   auth.currentUser?.email === 'admin@oversizehub.net' ||
                   auth.currentUser?.uid === 'jfprince@soltec.ca' ||
                   auth.currentUser?.uid === 'jfprince29@gmail.com' ||
                   auth.currentUser?.uid === 'admin@oversizehub.net';
    
    if (!isAdmin) {
      logInfo('Non-admin user generating schema - some collections may be inaccessible');
    } else {
      logInfo('Admin user detected, but client-side security rules still apply');
      logInfo('Note: Even admin users are restricted by security rules in the client SDK');
      logInfo('To access all collections, use the Firebase Admin SDK in a secure backend environment');
    }
    
    // Get all collection IDs from Firestore
    // Since we can't directly list collections in the client SDK,
    // we'll use a different approach to discover collections
    
    // First, try to query known top-level collections
    const knownCollections = [
      'surveys', 'pois', 'routes', 'track_points', 'userProfiles', 
      'userSettings', 'subscriptions', 'presence', 'shared_maps', 'emails',
      'bug_reports', 'team_messages', 'team_files', 'team_announcements', 'team_members',
      'mail', 'pendingUploads', 'system', 'system_settings', 'reports', 'notifications',
      'hauteurs_libres', 'hauteurs_libres_details', 'wfs_layers', 'module_access', 
      'stripe_customers', 'stripe_subscriptions', 'stripe_payment_methods', 'stripe_products',
      'stripe_prices', 'stripe_invoices', 'stripe_webhook_events', 'stripe_checkout_sessions', 
      'authorities', 'vehicle_types', 'forms', 'incidents', 'permits', 'team_announcements',
      'high_pole_pois', 'quick_survey_pois', 'book_surveys', 'shared_maps', 'team_messages',
      'team_files', 'team_announcements', 'user_activity', 'user_logs', 'user_preferences',
      'user_devices', 'user_sessions', 'user_tokens', 'user_notifications', 'user_settings',
      'user_profiles', 'user_permissions', 'user_roles', 'user_groups', 'user_invites',
      'user_feedback', 'user_reports', 'user_subscriptions', 'user_payments', 'user_invoices',
      'user_cards', 'user_addresses', 'user_contacts', 'user_notes', 'user_files',
      'user_photos', 'user_documents', 'user_signatures', 'user_certificates', 'user_licenses',
      'user_vehicles', 'user_trips', 'user_routes', 'user_locations', 'user_events'
    ];

    // Set initial progress
    schema.metadata.progress.totalCollections = knownCollections.length;
    schema.metadata.progress.status = 'scanning';
    
    // Process each known collection
    for (let i = 0; i < knownCollections.length; i++) {
      const collectionName = knownCollections[i];
      
      // Skip already processed collections
      if (processedCollections.has(collectionName)) {
        logInfo(`Collection already processed: ${collectionName}`);
        schema.metadata.progress.scannedCollections++;
        continue;
      }
      
      schema.metadata.progress.currentCollection = collectionName;
      logInfo(`Processing collection: ${collectionName} (${i+1}/${knownCollections.length})`);
      
      try {
        // Add timeout to prevent browser from freezing
        await Promise.race([
          processCollection(collectionName, schema, processedCollections, 0, isAdmin),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error(`Timeout processing collection ${collectionName}`)), 15000)
          )
        ]);
        
        schema.metadata.progress.scannedCollections++;
        
        // Emit progress event
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('schemaGeneratorProgress', { 
            detail: { 
              ...schema.metadata.progress,
              percentComplete: Math.round((schema.metadata.progress.scannedCollections / schema.metadata.progress.totalCollections) * 100)
            } 
          }));
        }
      } catch (err) {
        // Skip collections that don't exist or can't be accessed
        logInfo(`Skipping collection ${collectionName}: ${err.message}`);
        
        // Emit permission error event if it's a permission issue
        if (err.code === 'permission-denied' || err.message.includes('Missing or insufficient permissions')) {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('schemaGeneratorPermissionError', { 
              detail: { 
                collection: collectionName,
                error: err.message
              } 
            }));
          }
        }
        
        console.warn(`Skipping collection ${collectionName}:`, err);
        schema.metadata.progress.scannedCollections++;
        
        // Still emit progress event even if collection fails
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('schemaGeneratorProgress', { 
            detail: { 
              ...schema.metadata.progress,
              percentComplete: Math.round((schema.metadata.progress.scannedCollections / schema.metadata.progress.totalCollections) * 100)
            } 
          }));
        }
      }
    }

    // Try to discover additional collections using collectionGroup queries
    try {
      schema.metadata.progress.status = 'discovering subcollections';
      schema.metadata.progress.currentCollection = 'subcollections';
      
      logInfo('Discovering subcollections');
      
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('schemaGeneratorProgress', { 
          detail: { 
            ...schema.metadata.progress,
            percentComplete: 90
          } 
        }));
      }
      
      // Some common subcollection names to try
      const commonSubcollections = [
        'files', 'comments', 'history', 'logs', 'photos', 'measurements',
        'details', 'members', 'permissions', 'settings', 'metadata'
      ];

      for (const subCollName of commonSubcollections) {
        try {
          const groupQuery = query(collectionGroup(db, subCollName), limit(1));
          
          // Add timeout to prevent browser from freezing
          const groupSnapshot = await Promise.race([
            getDocs(groupQuery),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error(`Timeout querying collection group ${subCollName}`)), 5000)
            )
          ]);
          
          if (!groupSnapshot.empty) {
            // If we found documents, get their parent references to discover collections
            for (const docSnap of groupSnapshot.docs) {
              const path = docSnap.ref.path;
              const parentPath = path.split('/').slice(0, -2).join('/');
              
              if (parentPath && !processedCollections.has(parentPath)) {
                try {
                  await Promise.race([
                    processCollection(parentPath, schema, processedCollections, 0),
                    new Promise((_, reject) => 
                      setTimeout(() => reject(new Error(`Timeout processing subcollection ${parentPath}`)), 5000)
                    )
                  ]);
                } catch (subErr) {
                  logInfo(`Skipping subcollection ${parentPath}: ${subErr.message}`);
                }
              }
            }
          }
        } catch (err) {
          // Skip subcollections that can't be accessed
          if (err.code === 'permission-denied' || err.message.includes('Missing or insufficient permissions')) {
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('schemaGeneratorPermissionError', { 
                detail: { 
                  collection: `${subCollName} (collection group)`,
                  error: err.message
                } 
              }));
            }
          }
          logInfo(`Skipping subcollection group ${subCollName}: ${err.message}`);
        }
      }
    } catch (err) {
      logInfo(`Collection group queries not supported: ${err.message}`);
    }

    // Update metadata
    schema.metadata.totalCollections = Object.keys(schema.collections).length;
    schema.metadata.totalDocuments = countTotalDocuments(schema.collections);
    schema.metadata.scanDuration = Date.now() - startTime;
    
    // Add permission note if not admin
    if (!isAdmin) {
      schema.metadata.permissionNote = 'Schema generated by non-admin user. Some collections may be inaccessible due to permission restrictions.';
      schema.metadata.progress.status = 'completed with permission restrictions';
    } else {
      schema.metadata.progress.status = 'completed';
    }
    
    // Final progress event
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('schemaGeneratorProgress', { 
        detail: { 
          ...schema.metadata.progress,
          percentComplete: 100,
          status: 'completed'
        } 
      }));
    }

    logInfo(`Schema generated successfully`);
    
    // Add explanation about permissions
    schema.metadata.permissionNote = `
      Note: The Firebase client SDK has security restrictions that prevent listing all collections and accessing certain data, 
      even for admin users. This is a security feature of Firebase. To access all collections without restrictions, 
      you would need to use the Firebase Admin SDK in a secure backend environment.
    `;
    
    return schema;
  } catch (error) {
    logError('Error generating schema:', error);
    throw error;
  }
};

/**
 * Process a collection and add it to the schema
 * @param {string} collectionPath - The collection path
 * @param {Object} schema - The schema object
 * @param {Set} processedCollections - Set of already processed collections
 * @param {number} depth - Current recursion depth
 * @param {boolean} isAdmin - Whether the current user is an admin
 */
async function processCollection(collectionPath, schema, processedCollections, depth = 0, isAdmin = false) {
  // Prevent infinite recursion
  if (depth > 5) {
    logInfo(`Maximum recursion depth reached for ${collectionPath}`);
    return;
  }
  
  // Skip if already processed
  if (processedCollections.has(collectionPath)) {
    logInfo(`Collection already processed: ${collectionPath}`);
    return;
  }
  
  processedCollections.add(collectionPath);
  
  try {
    logInfo(`Getting collection reference for: ${collectionPath}`);
    
    // Get collection reference
    let collectionRef;
    
    try {
      collectionRef = collection(db, collectionPath);
    } catch (err) {
      logError(`Invalid collection reference for ${collectionPath}:`, err);
      throw new Error(`Invalid collection reference for ${collectionPath}: ${err.message}`);
    }
    
    // Get documents with timeout
    let docsSnapshot;
    try {
      // Add a note about permissions for admin users
      if (isAdmin) {
        logInfo(`Note: Even admin users are restricted by security rules in the client SDK for collection: ${collectionPath}`);
      }
      
      docsSnapshot = await Promise.race([
        getDocs(collectionRef),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error(`Timeout getting documents for ${collectionPath}`)), 8000)
        )
      ]);
    } catch (error) {
      // Check if this is a permission error
      if (error.code === 'permission-denied' || error.message.includes('Missing or insufficient permissions')) {
        // Emit permission error event
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('schemaGeneratorPermissionError', { 
            detail: { 
              collection: collectionPath,
              error: error.message
            } 
          }));
        }
      }
      throw error;
    }
    
    // Add collection to schema
    const pathParts = collectionPath.split('/');
    let currentLevel = schema.collections;
    
    // For nested collections, navigate to the correct level
    if (pathParts.length > 1) {
      for (let i = 0; i < pathParts.length; i += 2) {
        const collName = pathParts[i];
        
        if (i === pathParts.length - 1) {
          // Last part is the collection name
          if (!currentLevel[collName]) {
            currentLevel[collName] = {
              documentCount: 0,
              fields: {},
              subcollections: {}
            };
          }
        } else {
          // This is a parent collection
          if (!currentLevel[collName]) {
            currentLevel[collName] = {
              documentCount: 0,
              fields: {},
              subcollections: {}
            };
          }
          
          // Navigate to subcollections
          currentLevel = currentLevel[collName].subcollections;
          
          // If there's a document ID, we need to create a placeholder
          if (i + 1 < pathParts.length) {
            const docId = pathParts[i + 1];
            if (!currentLevel[`__doc_${docId}`]) {
              currentLevel[`__doc_${docId}`] = {
                documentCount: 1,
                fields: {},
                subcollections: {}
              };
            }
            currentLevel = currentLevel[`__doc_${docId}`].subcollections;
          }
        }
      }
    } else {
      // Top-level collection
      if (!currentLevel[collectionPath]) {
        currentLevel[collectionPath] = {
          documentCount: 0,
          fields: {},
          subcollections: {}
        };
      }
    }
    
    // Get the collection object in our schema
    let collectionObj = currentLevel[pathParts[pathParts.length - 1]];
    if (!collectionObj) {
      collectionObj = currentLevel[collectionPath];
    }
    
    // Update document count
    collectionObj.documentCount = docsSnapshot.size;
    schema.metadata.totalDocuments += docsSnapshot.size;
    
    logInfo(`Processed ${docsSnapshot.size} documents in collection ${collectionPath}`);

    // Emit document count progress
    if (typeof window !== 'undefined' && docsSnapshot.size > 0) {
      window.dispatchEvent(new CustomEvent('schemaGeneratorDocuments', { 
        detail: { 
          collection: collectionPath,
          count: docsSnapshot.size,
          totalDocuments: schema.metadata.totalDocuments
        } 
      }));
    }
    
    // Process documents to infer schema
    for (const docSnapshot of docsSnapshot.docs) {
      // Limit the number of documents processed to avoid browser freezing
      if (docsSnapshot.size > 100 && docsSnapshot.docs.indexOf(docSnapshot) > 20) {
        logInfo(`Limiting schema inference to first 20 documents for large collection ${collectionPath}`);
        break;
      }
      
      const data = docSnapshot.data();
      
      // Add fields to schema
      Object.entries(data).forEach(([fieldName, fieldValue]) => {
        if (!collectionObj.fields[fieldName]) {
          collectionObj.fields[fieldName] = {
            type: typeof fieldValue,
            nullable: fieldValue === null,
            isArray: Array.isArray(fieldValue),
            example: getSafeExample(fieldValue)
          };
        }
      });
      
      // Check for subcollections
      try {
        // We can't directly list subcollections in the client SDK,
        // but we can try some common subcollection names (limit to first 5 documents)
        if (docsSnapshot.docs.indexOf(docSnapshot) > 10) continue;
        
        const docPath = docSnapshot.ref.path;
        const commonSubcollections = [
          'files', 'comments', 'history', 'logs', 'photos', 'measurements',
          'details', 'members', 'permissions', 'settings', 'metadata', 'attachments',
          'notes', 'events', 'activities', 'transactions', 'messages', 'contacts',
          'addresses', 'payments', 'invoices', 'subscriptions', 'products', 'orders',
          'items', 'categories', 'tags', 'reviews', 'ratings', 'comments', 'likes',
          'shares', 'views', 'clicks', 'impressions', 'conversions', 'sessions',
          'devices', 'browsers', 'locations', 'referrers', 'campaigns', 'sources',
          'mediums', 'terms', 'contents', 'groups', 'roles', 'permissions', 'tokens',
          'logs', 'errors', 'warnings', 'info', 'debug', 'traces', 'metrics', 'stats',
          'analytics', 'reports', 'exports', 'imports', 'backups', 'restores', 'syncs',
          'webhooks', 'callbacks', 'notifications', 'alerts', 'reminders', 'schedules',
          'tasks', 'jobs', 'queues', 'workers', 'processes', 'threads', 'instances'
        ];
        
        for (const subCollName of commonSubcollections) {
          try {
            // Add timeout to prevent browser from freezing
            const subCollRef = collection(db, `${docPath}/${subCollName}`);
            const subCollSnapshot = await Promise.race([
              getDocs(query(subCollRef, limit(1))),
              new Promise((_, reject) => 
                setTimeout(() => reject(new Error(`Timeout checking subcollection ${subCollName}`)), 2000)
              )
            ]);
            
            if (!subCollSnapshot.empty) {
              // Recursively process this subcollection
              try {
                await Promise.race([
                  processCollection(`${docPath}/${subCollName}`, schema, processedCollections, depth + 1, isAdmin),
                  new Promise((_, reject) => 
                    setTimeout(() => reject(new Error(`Timeout processing subcollection ${subCollName}`)), 3000)
                  )
                ]);
              } catch (subErr) {
                logInfo(`Skipping subcollection ${docPath}/${subCollName}: ${subErr.message}`);
              }
            }
          } catch (err) {
            // Skip subcollections that don't exist or can't be accessed
          }
        }
      } catch (err) {
        // Skip subcollection discovery if it fails
      }
    }
    
    logInfo(`Processed collection: ${collectionPath} with ${docsSnapshot.size} documents`);
  } catch (error) {
    logError(`Error processing collection ${collectionPath}:`, error);
    throw error;
  }
}

/**
 * Get a safe example value for schema documentation
 * @param {any} value - The value to get an example of
 * @returns {any} A safe example value
 */
function getSafeExample(value) {
  // Handle circular references and complex objects
  try {
    JSON.stringify(value);
  } catch (err) {
    return '[Complex Object]';
  }
  
  if (value === null || value === undefined) {
    return null;
  }
  
  if (typeof value === 'string') {
    // Truncate long strings and mask potential sensitive data
    if (value.length > 50) {
      return value.substring(0, 47) + '...';
    }
    
    // Mask potential sensitive data
    if (
      value.includes('@') || // Email
      /^\d{10,}$/.test(value) || // Phone number
      /^[A-Za-z0-9]{20,}$/.test(value) // API key or token
    ) {
      return '[REDACTED]';
    }
    
    return value;
  }
  
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return [];
    }
    // For arrays of objects, analyze the structure
    if (typeof value[0] === 'object' && value[0] !== null) {
      // Take the first item as a sample
      const sampleItem = value[0];
      // Create a structure description
      const itemStructure = {};
      
      // Process the object properties
      Object.entries(sampleItem).forEach(([key, propValue]) => {
        itemStructure[key] = typeof propValue;
      });
      
      return [{
        type: 'object',
        structure: itemStructure
      }];
    }
    // For arrays of primitive types, return the type
    return [{
      type: typeof value[0],
      sample: value.length > 3 ? value.slice(0, 3).map(v => typeof v === 'object' ? '[Object]' : v) : value
    }];
  }
  
  if (typeof value === 'object') {
    // For objects, just return the keys (limit to 5 keys)
    const keys = Object.keys(value);
    return keys.length > 5 ? [...keys.slice(0, 5), '...'] : keys;
  }
  
  return typeof value;
}

/**
 * Count total documents in the schema
 * @param {Object} collections - The collections object
 * @returns {number} Total document count
 */
function countTotalDocuments(collections) {
  let count = 0;
  
  Object.values(collections).forEach(collection => {
    count += collection.documentCount;
    
    if (collection.subcollections) {
      count += countTotalDocuments(collection.subcollections);
    }
  });
  
  return count;
}

/**
 * Export schema to JSON file
 * @param {Object} schema - The schema to export
 */
export const exportSchemaToJSON = (schema) => {
  try {
    const blob = new Blob([JSON.stringify(schema, null, 2)], { type: 'application/json' });
    
    // Create a safe filename
    const timestamp = new Date().toISOString().replace(/:/g, '-').replace(/\..+/, '');
    const filename = `database-schema-${timestamp}.json`;
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    logInfo('Schema exported to JSON successfully');
    return true;
  } catch (error) {
    logError('Error exporting schema to JSON:', error);
    throw error;
  }
}

/**
 * Export schema to HTML file
 * @param {Object} schema - The schema to export
 */
export const exportSchemaAsHTML = (schema) => {
  try {
    // Create HTML representation of the schema
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Database Schema</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .collection { margin: 10px 0; padding: 10px; border: 1px solid #ccc; }
          .field { margin: 5px 0; padding-left: 20px; }
          .metadata { margin-bottom: 20px; }
        </style>
      </head>
      <body>
        <h1>Database Schema</h1>
        <div class="metadata">
          <p>Generated: ${new Date(schema.metadata.generatedAt).toLocaleString()}</p>
          <p>Total Collections: ${schema.metadata.totalCollections}</p>
          <p>Total Documents: ${schema.metadata.totalDocuments}</p>
          <p>Scan Duration: ${schema.metadata.scanDuration}ms</p>
        </div>
    `;

    // Function to render collections recursively
    const renderCollection = (collection, name, level = 0) => {
      const padding = level * 20;
      let collectionHtml = `
        <div class="collection" style="margin-left: ${padding}px">
          <h3>${name}</h3>
          <p>Document Count: ${collection.documentCount}</p>
          <h4>Fields:</h4>
      `;

      // Add fields
      Object.entries(collection.fields).forEach(([fieldName, fieldInfo]) => {
        collectionHtml += `
          <div class="field">
            <strong>${fieldName}</strong>: ${fieldInfo.type}
            ${fieldInfo.isArray ? ' (Array)' : ''}
            ${fieldInfo.isArray && fieldInfo.example && fieldInfo.example[0]?.structure ? 
              `<div style="margin-left: 20px;">
                <strong>Array items:</strong>
                <div style="margin-left: 20px;">
                  ${Object.entries(fieldInfo.example[0].structure).map(([key, type]) => 
                    `<div><strong>${key}</strong>: ${type}</div>`
                  ).join('')}
                </div>
              </div>` 
            : ''}
            ${fieldInfo.nullable ? ' (Nullable)' : ''}
            <br>
            Example: ${JSON.stringify(fieldInfo.example)}
          </div>
        `;
      });

      // Add subcollections recursively
      if (collection.subcollections && Object.keys(collection.subcollections).length > 0) {
        collectionHtml += '<h4>Subcollections:</h4>';
        Object.entries(collection.subcollections).forEach(([subName, subCollection]) => {
          collectionHtml += renderCollection(subCollection, subName, level + 1);
        });
      }

      collectionHtml += '</div>';
      return collectionHtml;
    };

    // Render all collections
    Object.entries(schema.collections).forEach(([name, collection]) => {
      html += renderCollection(collection, name);
    });

    html += `
      </body>
      </html>
    `;

    // Create and download the file
    const blob = new Blob([html], { type: 'text/html' });
    const timestamp = new Date().toISOString().replace(/:/g, '-').replace(/\..+/, '');
    const filename = `database-schema-${timestamp}.html`;
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    logInfo('Schema exported to HTML successfully');
    return true;
  } catch (error) {
    logError('Error exporting schema to HTML:', error);
    throw error;
  }
}